import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  FileText,
  FileCheck,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ShieldCheck,
  ShieldAlert,
  Upload,
  RefreshCw,
  Eye,
  X,
  ChevronRight,
  ArrowRight,
  Car,
  CreditCard,
  User,
  Info,
  Check,
  Sparkles,
  ExternalLink,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Layers,
} from 'lucide-react';
import { UserProfile } from '../../types';
import { CompanionDocumentRecord } from '../../types/verification.types';
import {
  SupportedDocumentType,
  CompanionDocumentEntry,
  CompanionProfileLifecycleStatus,
  DocumentValidationResult,
  MissingDocumentSideItem,
  ConfiguredDocumentRequirement,
  validateCompanionDocuments,
  validateDocumentSides,
  resolveActiveDocumentRequirements,
  DOCUMENT_REQUIREMENTS_REGISTRY,
  normalizeDocumentType,
  hasValidUploadedData,
  isPartUploading,
} from '../../utils/documentValidation';
import {
  subscribeToCompanionDocuments,
  subscribeToCompanionVerification,
  uploadDocumentSide,
  createDefaultDocumentRecord,
} from '../../lib/verificationService';
import { ProfileStatusBadge } from './ProfileStatusBadge';

export interface DocumentStatusSectionProps {
  userProfile?: UserProfile | null;
  companionId?: string;
  onNavigateToVerification?: () => void;
  className?: string;
  isEmbedded?: boolean;
}

export type DocumentFilterType = 'all' | 'missing' | 'uploaded';

export interface VisualDocumentSideStatus {
  documentId: SupportedDocumentType;
  documentTitle: string;
  side: 'front' | 'back';
  sideLabel: string;
  shortLabel: string;
  required: boolean;
  helpText: string;
  status: 'missing' | 'uploading' | 'uploaded' | 'verified' | 'rejected';
  statusLabel: string; // e.g. "Aadhaar Back: Missing" or "Aadhaar Front: Uploaded"
  dataUrl?: string;
  fileName?: string;
  uploadedAt?: string;
  rejectionReason?: string;
}

export const DocumentStatusSection: React.FC<DocumentStatusSectionProps> = ({
  userProfile,
  companionId: propCompanionId,
  onNavigateToVerification,
  className = '',
  isEmbedded = false,
}) => {
  // Resolve Effective Companion ID
  const effectiveCompanionId = useMemo(() => {
    if (propCompanionId) return propCompanionId;
    if ((userProfile as any)?.uid) return (userProfile as any).uid;
    if (userProfile?.email) return userProfile.email.replace(/[^a-zA-Z0-9]/g, '_');
    return 'CP1024';
  }, [propCompanionId, userProfile]);

  // Real-time Firestore & Local Documents State
  const [liveDocs, setLiveDocs] = useState<Record<string, any>>(() => {
    try {
      const cached = localStorage.getItem(`pairup_verification_docs_${effectiveCompanionId}`);
      if (cached) return JSON.parse(cached);
    } catch {}
    return {};
  });

  const [liveVerificationProfile, setLiveVerificationProfile] = useState<any>(null);
  const [filter, setFilter] = useState<DocumentFilterType>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [uploadNotice, setUploadNotice] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Direct Upload State
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingUploadTarget, setPendingUploadTarget] = useState<{
    docType: SupportedDocumentType;
    side: 'front' | 'back';
    title: string;
  } | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Preview Lightbox Modal
  const [previewImage, setPreviewImage] = useState<{
    url: string;
    title: string;
    side: string;
  } | null>(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewRotation, setPreviewRotation] = useState(0);

  // Subscribe to real-time document and profile updates from Firestore
  useEffect(() => {
    if (!effectiveCompanionId) return;

    const unsubDocs = subscribeToCompanionDocuments(effectiveCompanionId as any, (docs) => {
      setLiveDocs(docs);
    });

    const unsubProfile = subscribeToCompanionVerification(effectiveCompanionId, (p) => {
      setLiveVerificationProfile(p);
    });

    return () => {
      unsubDocs();
      unsubProfile();
    };
  }, [effectiveCompanionId]);

  // Derive consolidated document map incorporating userProfile legacy fallback fields
  const documentsMap = useMemo<Record<string, CompanionDocumentEntry>>(() => {
    const map: Record<string, CompanionDocumentEntry> = { ...liveDocs };

    // Driving licence fallback from userProfile
    const dlDetails = userProfile?.drivingLicenceDetails || userProfile?.companionProfile?.drivingLicence;
    if (dlDetails) {
      const dlKey = 'driving_license';
      const existingDl = map[dlKey] || {};
      map[dlKey] = {
        ...existingDl,
        idNumber: dlDetails.licenceNumber || (existingDl as any).idNumber,
        front: dlDetails.frontUrl || existingDl.front,
        back: dlDetails.backUrl || existingDl.back,
      };
    }

    // Primary ID fallback from userProfile
    const compPrimaryDoc = userProfile?.companionProfile;
    if (compPrimaryDoc?.idType && compPrimaryDoc?.idDocumentUrl) {
      const normalizedType = normalizeDocumentType(compPrimaryDoc.idType);
      const existing = map[normalizedType] || {};
      map[normalizedType] = {
        ...existing,
        idNumber: compPrimaryDoc.idNumber || (existing as any).idNumber,
        front: existing.front || compPrimaryDoc.idDocumentUrl,
        back: existing.back || (compPrimaryDoc as any).backUrl || (compPrimaryDoc as any).idBackDocumentUrl,
      };
    }

    return map;
  }, [liveDocs, userProfile]);

  // Context flags
  const isDriver = useMemo(() => {
    const services =
      userProfile?.companionProfile?.services ||
      (userProfile?.services ? userProfile.services.map((s) => s.name) : []);
    return (
      Boolean(userProfile?.isDriverApproved) ||
      services.some((s) => typeof s === 'string' && s.toLowerCase().includes('driver'))
    );
  }, [userProfile]);

  const selectedPrimaryDocType = useMemo(() => {
    return (
      userProfile?.companionProfile?.idType ||
      liveVerificationProfile?.primaryDocType ||
      'aadhaar'
    );
  }, [userProfile, liveVerificationProfile]);

  // Active document requirements resolved by centralized validation utility
  const activeRequirements = useMemo<ConfiguredDocumentRequirement[]>(() => {
    return resolveActiveDocumentRequirements({
      selectedPrimaryDocType,
      isDriverService: isDriver,
      selectedServices: userProfile?.companionProfile?.services || [],
    });
  }, [selectedPrimaryDocType, isDriver, userProfile]);

  // Centralized Document Validation Execution
  const validationResult: DocumentValidationResult = useMemo(() => {
    const currentAdminVerdict =
      liveVerificationProfile?.verificationStatus === 'verified' || userProfile?.isIdVerified
        ? 'verified'
        : liveVerificationProfile?.verificationStatus === 'rejected'
        ? 'rejected'
        : null;

    const isProfileInfoComplete = Boolean(
      userProfile?.displayName || userProfile?.name || userProfile?.companionProfile?.displayName
    );

    const verificationStarted =
      liveVerificationProfile?.verificationStatus === 'under_review' ||
      liveVerificationProfile?.verificationStatus === 'submitted';

    return validateCompanionDocuments(documentsMap, {
      selectedPrimaryDocType,
      isDriverService: isDriver,
      isProfileInfoComplete,
      verificationStarted,
      currentAdminVerdict,
    });
  }, [documentsMap, selectedPrimaryDocType, isDriver, userProfile, liveVerificationProfile]);

  // Build granular side-by-side status list for each required document
  const detailedSidesStatusList = useMemo<VisualDocumentSideStatus[]>(() => {
    const items: VisualDocumentSideStatus[] = [];

    for (const req of activeRequirements) {
      const normalizedId = normalizeDocumentType(req.id);
      const docEntry = documentsMap[req.id] || documentsMap[normalizedId];
      const registryEntry = DOCUMENT_REQUIREMENTS_REGISTRY[normalizedId] || DOCUMENT_REQUIREMENTS_REGISTRY['aadhaar'];

      for (const part of req.parts) {
        const partData = part.side === 'front' ? docEntry?.front : docEntry?.back;
        const registryPart = registryEntry.parts.find((p) => p.side === part.side) || {
          shortLabel: part.side === 'front' ? 'Front Side' : 'Back Side',
          helpText: part.helpText || '',
        };

        const shortLabel = part.side === 'front' ? 'Front' : 'Back';
        const docTitle = req.title;

        let status: VisualDocumentSideStatus['status'] = 'missing';
        let statusLabel = `${docTitle} ${shortLabel}: Missing`;
        let dataUrl: string | undefined = undefined;
        let fileName: string | undefined = undefined;
        let uploadedAt: string | undefined = undefined;

        if (isPartUploading(partData)) {
          status = 'uploading';
          statusLabel = `${docTitle} ${shortLabel}: Uploading...`;
        } else if (hasValidUploadedData(partData)) {
          // Check if admin verified the document
          if (
            liveVerificationProfile?.verificationStatus === 'verified' ||
            (docEntry as any)?.status === 'verified' ||
            userProfile?.isIdVerified
          ) {
            status = 'verified';
            statusLabel = `${docTitle} ${shortLabel}: Verified`;
          } else if (
            (docEntry as any)?.status === 'rejected' ||
            (docEntry as any)?.status === 'reupload_required'
          ) {
            status = 'rejected';
            statusLabel = `${docTitle} ${shortLabel}: Re-upload Required`;
          } else {
            status = 'uploaded';
            statusLabel = `${docTitle} ${shortLabel}: Uploaded`;
          }

          // Extract candidate URL or file name
          if (typeof partData === 'string') {
            dataUrl = partData;
          } else if (typeof partData === 'object' && partData !== null) {
            dataUrl = partData.downloadUrl || partData.url || partData.previewUrl;
            fileName = partData.fileName;
            uploadedAt = partData.uploadedAt;
          }
        } else {
          status = 'missing';
          // Explicit standard label e.g., 'Aadhaar Back: Missing'
          statusLabel = `${docTitle} ${shortLabel}: Missing`;
        }

        items.push({
          documentId: normalizedId,
          documentTitle: docTitle,
          side: part.side,
          sideLabel: part.label,
          shortLabel: registryPart.shortLabel || `${shortLabel} Side`,
          required: part.required,
          helpText: part.helpText || registryPart.helpText || '',
          status,
          statusLabel,
          dataUrl,
          fileName,
          uploadedAt,
          rejectionReason: (docEntry as any)?.rejectionReason || undefined,
        });
      }
    }

    return items;
  }, [activeRequirements, documentsMap, liveVerificationProfile, userProfile]);

  // Aggregate metrics
  const totalRequiredSides = detailedSidesStatusList.filter((s) => s.required).length;
  const missingRequiredSides = detailedSidesStatusList.filter((s) => s.required && s.status === 'missing');
  const uploadedRequiredSides = detailedSidesStatusList.filter((s) => s.required && (s.status === 'uploaded' || s.status === 'verified'));
  const uploadingRequiredSides = detailedSidesStatusList.filter((s) => s.required && s.status === 'uploading');
  const completionPercentage = totalRequiredSides > 0
    ? Math.round((uploadedRequiredSides.length / totalRequiredSides) * 100)
    : 100;

  // Filtered sides based on active tab
  const filteredSides = useMemo(() => {
    if (filter === 'missing') {
      return detailedSidesStatusList.filter((s) => s.status === 'missing' || s.status === 'rejected');
    }
    if (filter === 'uploaded') {
      return detailedSidesStatusList.filter((s) => s.status === 'uploaded' || s.status === 'verified');
    }
    return detailedSidesStatusList;
  }, [detailedSidesStatusList, filter]);

  // Refresh handler
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const cached = localStorage.getItem(`pairup_verification_docs_${effectiveCompanionId}`);
      if (cached) {
        setLiveDocs(JSON.parse(cached));
      }
      setUploadNotice({ message: 'Document validation synchronized with Firestore.', type: 'info' });
      setTimeout(() => setUploadNotice(null), 3000);
    } catch {
      // error handled
    } finally {
      setIsRefreshing(false);
    }
  };

  // Direct Upload Trigger
  const triggerSideUpload = (docType: SupportedDocumentType, side: 'front' | 'back', title: string) => {
    setPendingUploadTarget({ docType, side, title });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  // File Input Change Handler
  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !pendingUploadTarget) return;

    setIsUploading(true);
    setUploadNotice({ message: `Uploading ${pendingUploadTarget.title} (${pendingUploadTarget.side} side)...`, type: 'info' });

    try {
      const updatedRecord = await uploadDocumentSide(
        effectiveCompanionId,
        pendingUploadTarget.docType,
        pendingUploadTarget.side,
        file
      );

      // Instantly update local state for reactive validation
      setLiveDocs((prev) => ({
        ...prev,
        [pendingUploadTarget.docType]: updatedRecord,
      }));

      const sideName = pendingUploadTarget.side === 'front' ? 'Front' : 'Back';
      setUploadNotice({
        message: `Successfully uploaded ${pendingUploadTarget.title} ${sideName}! Validation updated.`,
        type: 'success',
      });
      setTimeout(() => setUploadNotice(null), 4000);
    } catch (err: any) {
      setUploadNotice({
        message: err?.message || 'Failed to upload document file.',
        type: 'error',
      });
      setTimeout(() => setUploadNotice(null), 5000);
    } finally {
      setIsUploading(false);
      setPendingUploadTarget(null);
    }
  };

  return (
    <section
      data-testid="companion-document-status-section"
      className={`space-y-6 animate-in fade-in duration-200 ${className}`}
    >
      {/* Hidden File Input for Direct Side Uploads */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelected}
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
      />

      {/* Floating Notice / Toast */}
      {uploadNotice && (
        <div
          className={`p-3.5 rounded-2xl text-xs flex items-center justify-between gap-2 shadow-lg animate-in slide-in-from-top-2 duration-150 ${
            uploadNotice.type === 'success'
              ? 'bg-emerald-950 border border-emerald-800 text-emerald-300'
              : uploadNotice.type === 'error'
              ? 'bg-rose-950 border border-rose-800 text-rose-300'
              : 'bg-blue-950 border border-blue-800 text-blue-300'
          }`}
        >
          <div className="flex items-center gap-2">
            {uploadNotice.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            ) : uploadNotice.type === 'error' ? (
              <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
            ) : (
              <RefreshCw className="w-4 h-4 text-blue-400 animate-spin shrink-0" />
            )}
            <span>{uploadNotice.message}</span>
          </div>
          <button
            type="button"
            onClick={() => setUploadNotice(null)}
            className="text-slate-400 hover:text-white cursor-pointer"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ========================================================= */}
      {/* SECTION HEADER & CONTROL BAR */}
      {/* ========================================================= */}
      <div className="p-6 rounded-3xl bg-slate-900 border border-slate-800 shadow-xl space-y-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="p-2 rounded-xl bg-purple-950/80 border border-purple-800/80 text-purple-400">
                <FileText className="w-5 h-5" />
              </span>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-black text-white tracking-tight">
                    Document Status
                  </h2>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider bg-purple-950 border border-purple-800 text-purple-300">
                    Dual-Side Compliance
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                  Centralized verification utility inspecting mandatory front & back identity uploads.
                </p>
              </div>
            </div>
          </div>

          {/* Badges & Actions */}
          <div className="flex flex-wrap items-center gap-2.5">
            <ProfileStatusBadge
              profileStatus={validationResult.profileStatus}
              userProfile={userProfile}
              missingCount={missingRequiredSides.length}
              onNavigateToVerification={onNavigateToVerification}
            />

            <button
              type="button"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="px-3 py-1.5 rounded-xl text-xs font-bold font-mono bg-slate-950 hover:bg-slate-800 border border-slate-800 text-slate-300 flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
              title="Refresh status from Firestore"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin text-purple-400' : ''}`} />
              <span>Sync</span>
            </button>

            {onNavigateToVerification && (
              <button
                type="button"
                onClick={onNavigateToVerification}
                className="px-3.5 py-1.5 rounded-xl text-xs font-bold bg-purple-600 hover:bg-purple-500 text-white flex items-center gap-1.5 transition-all shadow-md shadow-purple-950 cursor-pointer"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Verification Center</span>
                <ChevronRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* METRICS & PROGRESS BAR */}
        <div className="pt-2 border-t border-slate-800/80">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800">
              <span className="text-[10px] font-mono uppercase text-slate-400 font-bold block">
                Required Documents
              </span>
              <span className="text-xl font-black text-white font-mono mt-0.5 block">
                {activeRequirements.length}
              </span>
              <span className="text-[10px] text-slate-400 font-mono">
                {isDriver ? 'Primary ID + Driver Licence' : 'Government Primary ID'}
              </span>
            </div>

            <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800">
              <span className="text-[10px] font-mono uppercase text-slate-400 font-bold block">
                Total Required Sides
              </span>
              <span className="text-xl font-black text-blue-400 font-mono mt-0.5 block">
                {totalRequiredSides}
              </span>
              <span className="text-[10px] text-blue-400/80 font-mono">
                Front + Back Sides
              </span>
            </div>

            <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800">
              <span className="text-[10px] font-mono uppercase text-slate-400 font-bold block">
                Uploaded & Ready
              </span>
              <span className="text-xl font-black text-emerald-400 font-mono mt-0.5 block">
                {uploadedRequiredSides.length}
              </span>
              <span className="text-[10px] text-emerald-400/80 font-mono">
                {completionPercentage}% Complete
              </span>
            </div>

            <div className={`p-3.5 rounded-2xl border ${
              missingRequiredSides.length > 0
                ? 'bg-rose-950/40 border-rose-800/70'
                : 'bg-slate-950 border-slate-800'
            }`}>
              <span className="text-[10px] font-mono uppercase text-slate-400 font-bold block">
                Missing Sides
              </span>
              <span className={`text-xl font-black font-mono mt-0.5 block ${
                missingRequiredSides.length > 0 ? 'text-rose-400 animate-pulse' : 'text-slate-400'
              }`}>
                {missingRequiredSides.length}
              </span>
              <span className={`text-[10px] font-mono ${
                missingRequiredSides.length > 0 ? 'text-rose-300' : 'text-slate-400'
              }`}>
                {missingRequiredSides.length > 0 ? 'Action Required' : 'All Sides Complete'}
              </span>
            </div>
          </div>

          {/* Progress bar */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs font-mono">
              <span className="text-slate-400 flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-purple-400" />
                <span>Overall Document Completeness</span>
              </span>
              <span className="font-bold text-white">
                {uploadedRequiredSides.length} of {totalRequiredSides} Sides Uploaded ({completionPercentage}%)
              </span>
            </div>
            <div className="w-full h-2.5 bg-slate-950 rounded-full overflow-hidden border border-slate-800">
              <div
                className={`h-full transition-all duration-500 rounded-full ${
                  completionPercentage === 100
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-400'
                    : completionPercentage > 0
                    ? 'bg-gradient-to-r from-purple-500 to-indigo-500'
                    : 'bg-rose-500'
                }`}
                style={{ width: `${Math.max(5, completionPercentage)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ========================================================= */}
      {/* ACTION REQUIRED BANNER: WHEN DOCUMENTS INCOMPLETE */}
      {/* ========================================================= */}
      {missingRequiredSides.length > 0 && (
        <div
          data-testid="document-status-incomplete-alert"
          className="p-5 rounded-3xl bg-gradient-to-r from-amber-950/80 via-slate-900 to-rose-950/80 border border-amber-600/70 shadow-xl space-y-3.5 animate-in fade-in duration-150"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-2xl bg-amber-500/20 text-amber-400 border border-amber-500/30 shrink-0 mt-0.5">
                <AlertTriangle className="w-5 h-5 animate-bounce" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-black text-amber-200">
                    Documents Incomplete — Profile Activation Blocked
                  </h3>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-amber-950 border border-amber-700 text-amber-300">
                    {missingRequiredSides.length} Missing
                  </span>
                </div>
                <p className="text-xs text-slate-300 leading-relaxed">
                  PairUpToday security compliance strictly requires both sides of government identity documents.
                  The following required items must be uploaded before your profile can be activated for public bookings:
                </p>
              </div>
            </div>
          </div>

          {/* Explicit visual list of missing documents */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
            {missingRequiredSides.map((item) => (
              <div
                key={`${item.documentId}-${item.side}`}
                className="p-3 rounded-2xl bg-slate-950/90 border border-rose-900/60 flex items-center justify-between gap-2.5"
              >
                <div className="space-y-0.5 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-rose-500 animate-ping" />
                    <span className="text-xs font-black font-mono text-rose-300 truncate">
                      {item.statusLabel}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 truncate pl-4">
                    {item.helpText || `${item.shortLabel} of ${item.documentTitle} is mandatory`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => triggerSideUpload(item.documentId, item.side, item.documentTitle)}
                  disabled={isUploading}
                  className="px-3 py-1.5 rounded-xl text-xs font-bold font-mono bg-rose-600 hover:bg-rose-500 text-white shrink-0 flex items-center gap-1.5 shadow-md shadow-rose-950 cursor-pointer disabled:opacity-50"
                >
                  <Upload className="w-3.5 h-3.5" />
                  <span>Upload</span>
                </button>
              </div>
            ))}
          </div>

          <div className="pt-2 border-t border-amber-900/40 flex items-center justify-between text-xs text-amber-300/80">
            <span>UIDAI Aadhaar QR code & address sides are mandatory for background verification.</span>
            {onNavigateToVerification && (
              <button
                type="button"
                onClick={onNavigateToVerification}
                className="font-bold underline hover:text-white flex items-center gap-1 cursor-pointer"
              >
                <span>Go to Verification Center</span>
                <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* FILTER TABS & DOCUMENT REQUIREMENTS LIST */}
      {/* ========================================================= */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-black text-white">
              Required Documents Checklist
            </h3>
            <span className="text-xs text-slate-400">
              ({detailedSidesStatusList.length} total sides)
            </span>
          </div>

          {/* Filter Pills */}
          <div className="flex items-center gap-1 bg-slate-950 p-1 rounded-2xl border border-slate-800">
            <button
              type="button"
              onClick={() => setFilter('all')}
              className={`px-3 py-1 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                filter === 'all'
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              All ({detailedSidesStatusList.length})
            </button>
            <button
              type="button"
              onClick={() => setFilter('missing')}
              className={`px-3 py-1 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                filter === 'missing'
                  ? 'bg-rose-600 text-white shadow-sm'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <span>Missing</span>
              {missingRequiredSides.length > 0 && (
                <span className="w-4 h-4 rounded-full bg-rose-950 border border-rose-500 text-[10px] font-mono flex items-center justify-center font-bold">
                  {missingRequiredSides.length}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setFilter('uploaded')}
              className={`px-3 py-1 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                filter === 'uploaded'
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Uploaded ({uploadedRequiredSides.length})
            </button>
          </div>
        </div>

        {/* VISUAL CARDS FOR EACH DOCUMENT REQUIREMENT */}
        <div className="space-y-4">
          {activeRequirements.map((req) => {
            const normalizedId = normalizeDocumentType(req.id);
            const docSides = detailedSidesStatusList.filter((s) => s.documentId === normalizedId);
            const displayedSides = docSides.filter((s) => {
              if (filter === 'missing') return s.status === 'missing' || s.status === 'rejected';
              if (filter === 'uploaded') return s.status === 'uploaded' || s.status === 'verified';
              return true;
            });

            if (displayedSides.length === 0) return null;

            const isAllSidesComplete = docSides.every((s) => s.status === 'uploaded' || s.status === 'verified');
            const hasMissingSide = docSides.some((s) => s.required && s.status === 'missing');

            return (
              <div
                key={req.id}
                data-testid={`document-card-${req.id}`}
                className={`p-6 rounded-3xl bg-slate-900 border transition-all duration-200 shadow-xl space-y-5 ${
                  hasMissingSide
                    ? 'border-amber-800/80 hover:border-amber-700'
                    : 'border-slate-800 hover:border-slate-700'
                }`}
              >
                {/* Document Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-2xl border ${
                      normalizedId === 'driving_license'
                        ? 'bg-blue-950/80 border-blue-800 text-blue-400'
                        : normalizedId === 'pan'
                        ? 'bg-amber-950/80 border-amber-800 text-amber-400'
                        : 'bg-purple-950/80 border-purple-800 text-purple-400'
                    }`}>
                      {normalizedId === 'driving_license' ? (
                        <Car className="w-5 h-5" />
                      ) : (
                        <CreditCard className="w-5 h-5" />
                      )}
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-lg font-black text-white">
                          {req.title}
                        </h4>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider ${
                          req.required
                            ? 'bg-purple-950 border border-purple-800 text-purple-300'
                            : 'bg-slate-950 border border-slate-800 text-slate-400'
                        }`}>
                          {req.required ? 'Mandatory Requirement' : 'Conditional'}
                        </span>
                        {req.isConditional && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-blue-950 border border-blue-800 text-blue-300">
                            Driver Service
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {req.description || 'Government-issued national identity documentation'}
                      </p>
                    </div>
                  </div>

                  {/* Document Overall Status Badge */}
                  <div className="flex items-center gap-2">
                    {isAllSidesComplete ? (
                      <span className="px-3 py-1 rounded-full text-xs font-bold font-mono bg-emerald-950 border border-emerald-700 text-emerald-300 flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                        <span>All Sides Complete</span>
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-bold font-mono bg-rose-950 border border-rose-700 text-rose-300 flex items-center gap-1.5 animate-pulse">
                        <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />
                        <span>Sides Missing</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* SIDES LIST & CURRENT UPLOAD STATUS */}
                <div className="space-y-3">
                  <span className="text-xs font-bold font-mono uppercase text-slate-400 tracking-wider block">
                    Sides & Pages Breakdown:
                  </span>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                    {displayedSides.map((side) => {
                      const isMissing = side.status === 'missing';
                      const isUploadingSide = side.status === 'uploading';
                      const isUploaded = side.status === 'uploaded';
                      const isVerified = side.status === 'verified';
                      const isRejected = side.status === 'rejected';

                      return (
                        <div
                          key={`${side.documentId}-${side.side}`}
                          data-testid={`document-side-row-${side.documentId}-${side.side}`}
                          className={`p-4 rounded-2xl border transition-all flex flex-col justify-between gap-3 ${
                            isMissing
                              ? 'bg-rose-950/20 border-rose-900/60 hover:border-rose-700'
                              : isUploaded || isVerified
                              ? 'bg-slate-950/80 border-slate-800 hover:border-emerald-800/60'
                              : 'bg-slate-950 border-slate-800'
                          }`}
                        >
                          {/* Side Header & Explicit Status Label */}
                          <div className="space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <span className={`w-6 h-6 rounded-lg text-xs font-mono font-bold flex items-center justify-center ${
                                  side.side === 'front'
                                    ? 'bg-purple-950 text-purple-300 border border-purple-800'
                                    : 'bg-indigo-950 text-indigo-300 border border-indigo-800'
                                }`}>
                                  {side.side === 'front' ? 'F' : 'B'}
                                </span>
                                <div>
                                  <span className="text-sm font-black text-white block">
                                    {side.sideLabel}
                                  </span>
                                  <span className="text-[11px] text-slate-400">
                                    {side.shortLabel}
                                  </span>
                                </div>
                              </div>

                              {/* VISUAL STATUS INDICATOR (e.g. 'Aadhaar Back: Missing') */}
                              <div>
                                {isMissing && (
                                  <span
                                    data-testid="status-missing-badge"
                                    className="px-3 py-1 rounded-full text-xs font-bold font-mono bg-rose-950/90 border border-rose-600 text-rose-300 flex items-center gap-1.5 shadow-sm shadow-rose-950"
                                  >
                                    <AlertTriangle className="w-3.5 h-3.5 text-rose-400 shrink-0" />
                                    <span>{side.statusLabel}</span>
                                  </span>
                                )}

                                {isUploadingSide && (
                                  <span
                                    data-testid="status-uploading-badge"
                                    className="px-3 py-1 rounded-full text-xs font-bold font-mono bg-blue-950/90 border border-blue-600 text-blue-300 flex items-center gap-1.5"
                                  >
                                    <RefreshCw className="w-3.5 h-3.5 text-blue-400 animate-spin shrink-0" />
                                    <span>{side.statusLabel}</span>
                                  </span>
                                )}

                                {isUploaded && (
                                  <span
                                    data-testid="status-uploaded-badge"
                                    className="px-3 py-1 rounded-full text-xs font-bold font-mono bg-emerald-950/90 border border-emerald-600 text-emerald-300 flex items-center gap-1.5 shadow-sm shadow-emerald-950"
                                  >
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                                    <span>{side.statusLabel}</span>
                                  </span>
                                )}

                                {isVerified && (
                                  <span
                                    data-testid="status-verified-badge"
                                    className="px-3 py-1 rounded-full text-xs font-bold font-mono bg-purple-950/90 border border-purple-600 text-purple-300 flex items-center gap-1.5 shadow-sm shadow-purple-950"
                                  >
                                    <ShieldCheck className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                                    <span>{side.statusLabel}</span>
                                  </span>
                                )}

                                {isRejected && (
                                  <span
                                    data-testid="status-rejected-badge"
                                    className="px-3 py-1 rounded-full text-xs font-bold font-mono bg-rose-950/90 border border-rose-600 text-rose-300 flex items-center gap-1.5"
                                  >
                                    <ShieldAlert className="w-3.5 h-3.5 text-rose-400 shrink-0" />
                                    <span>{side.statusLabel}</span>
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Help Text / Requirement Description */}
                            <p className="text-xs text-slate-400 leading-relaxed pl-8">
                              {side.helpText}
                            </p>

                            {/* Rejection notice if any */}
                            {isRejected && side.rejectionReason && (
                              <div className="p-2.5 rounded-xl bg-rose-950/60 border border-rose-800 text-[11px] text-rose-300 flex items-center gap-2">
                                <Info className="w-3.5 h-3.5 shrink-0 text-rose-400" />
                                <span>Rejection Reason: {side.rejectionReason}</span>
                              </div>
                            )}
                          </div>

                          {/* Side Actions & Thumbnail Footer */}
                          <div className="pt-2.5 border-t border-slate-800/80 flex items-center justify-between gap-2">
                            {/* Thumbnail / Preview trigger if dataUrl exists */}
                            {side.dataUrl ? (
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPreviewImage({
                                      url: side.dataUrl!,
                                      title: side.documentTitle,
                                      side: side.shortLabel,
                                    })
                                  }
                                  className="group relative w-10 h-7 rounded-lg overflow-hidden border border-slate-700 bg-slate-900 cursor-pointer shrink-0"
                                  title="Click to preview uploaded image"
                                >
                                  {side.dataUrl.startsWith('data:application/pdf') ? (
                                    <div className="w-full h-full flex items-center justify-center text-[8px] font-mono text-purple-300 font-bold bg-purple-950">
                                      PDF
                                    </div>
                                  ) : (
                                    <img
                                      src={side.dataUrl}
                                      alt={`${side.documentTitle} ${side.side}`}
                                      className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                                    />
                                  )}
                                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                    <Eye className="w-3 h-3 text-white" />
                                  </div>
                                </button>
                                <span className="text-[11px] font-mono text-slate-400 truncate max-w-[140px]">
                                  {side.fileName || 'Uploaded image'}
                                </span>
                              </div>
                            ) : (
                              <span className="text-[11px] font-mono text-slate-400 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                <span>Pending upload</span>
                              </span>
                            )}

                            {/* Action Buttons */}
                            <div className="flex items-center gap-1.5">
                              {side.dataUrl && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPreviewImage({
                                      url: side.dataUrl!,
                                      title: side.documentTitle,
                                      side: side.shortLabel,
                                    })
                                  }
                                  className="px-2.5 py-1.5 rounded-xl text-xs font-bold text-slate-400 hover:text-white bg-slate-900 hover:bg-slate-800 border border-slate-800 cursor-pointer flex items-center gap-1"
                                >
                                  <Eye className="w-3 h-3" />
                                  <span>Preview</span>
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={() =>
                                  triggerSideUpload(side.documentId, side.side, side.documentTitle)
                                }
                                disabled={isUploading}
                                className={`px-3 py-1.5 rounded-xl text-xs font-bold font-mono transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50 ${
                                  isMissing
                                    ? 'bg-rose-600 hover:bg-rose-500 text-white shadow-md shadow-rose-950'
                                    : 'bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300'
                                }`}
                              >
                                <Upload className="w-3 h-3" />
                                <span>{isMissing ? 'Upload Now' : 'Replace'}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ========================================================= */}
      {/* SECURITY & UIDAI POLICY COMPLIANCE FOOTER */}
      {/* ========================================================= */}
      <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800 space-y-2 text-xs text-slate-400">
        <div className="flex items-center gap-2 text-purple-300 font-bold">
          <ShieldCheck className="w-4 h-4 text-purple-400" />
          <span>PairUpToday Identity & KYC Standards</span>
        </div>
        <p className="leading-relaxed">
          In compliance with UIDAI regulations and platform security protocols, <strong>Aadhaar Card</strong> requires
          both Front (photo, full name, 12-digit number) and Back (residential address and QR code). Driving licences
          require transport endorsement classes. All files are encrypted at rest with AES-256 and audited by platform administrators.
        </p>
      </div>

      {/* ========================================================= */}
      {/* LIGHTBOX PREVIEW MODAL */}
      {/* ========================================================= */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-150"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="relative max-w-3xl w-full bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-2xl space-y-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-base font-black text-white">
                  {previewImage.title} ({previewImage.side})
                </h3>
                <span className="text-xs text-slate-400 font-mono">
                  Encrypted KYC Document Preview
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPreviewZoom((z) => Math.max(0.5, z - 0.25))}
                  className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer"
                  title="Zoom Out"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewZoom((z) => Math.min(2.5, z + 0.25))}
                  className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer"
                  title="Zoom In"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewRotation((r) => (r + 90) % 360)}
                  className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 cursor-pointer"
                  title="Rotate"
                >
                  <RotateCw className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewImage(null)}
                  className="p-2 rounded-xl bg-rose-950 text-rose-300 hover:bg-rose-900 cursor-pointer"
                  title="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="w-full h-96 flex items-center justify-center bg-slate-950 rounded-2xl overflow-hidden border border-slate-800">
              {previewImage.url.startsWith('data:application/pdf') ? (
                <div className="p-8 text-center space-y-3">
                  <FileText className="w-12 h-12 text-purple-400 mx-auto" />
                  <p className="text-sm font-bold text-white">PDF Document Attached</p>
                  <a
                    href={previewImage.url}
                    download={`${previewImage.title}_${previewImage.side}.pdf`}
                    className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold inline-flex items-center gap-1.5"
                  >
                    <span>Download PDF</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              ) : (
                <img
                  src={previewImage.url}
                  alt={previewImage.title}
                  className="max-h-full max-w-full object-contain transition-transform duration-200"
                  style={{
                    transform: `scale(${previewZoom}) rotate(${previewRotation}deg)`,
                  }}
                />
              )}
            </div>

            <div className="flex items-center justify-between text-xs text-slate-400 font-mono">
              <span>Zoom: {Math.round(previewZoom * 100)}% • Rotation: {previewRotation}°</span>
              <button
                type="button"
                onClick={() => setPreviewImage(null)}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold cursor-pointer"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default DocumentStatusSection;
